import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Inbox, RefreshCw, CalendarX2, X, Download } from "lucide-react";
import BriefingsPanel, { exportCsv } from "../components/admin/BriefingsPanel";
import PortalAdminPanel from "../components/admin/PortalAdminPanel";
import { useNoIndex } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const formatDetail = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Something went wrong. Please try again.";
};

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-line bg-surface1 p-8 md:p-10">
        <div className="flex items-center gap-3 mb-8">
          <img src="/bigibila-logo.png" alt="Bigibila" className="h-10 w-10" />
          <div>
            <p className="font-serif text-xl">Bigibila</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">Admin access</p>
          </div>
        </div>
        <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2" htmlFor="admin-email">Email</label>
        <input id="admin-email" type="email" data-testid="admin-email-input" className="field-dark w-full px-4 py-3 text-sm mb-5" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@bigibila.com.au" />
        <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2" htmlFor="admin-password">Password</label>
        <input id="admin-password" type="password" data-testid="admin-password-input" className="field-dark w-full px-4 py-3 text-sm mb-8" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        <button type="submit" disabled={busy} data-testid="admin-login-btn" className="w-full font-mono text-sm uppercase tracking-[0.2em] px-6 py-3.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-60">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const BookingsPanel = () => {
  const [bookings, setBookings] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [blockDate, setBlockDate] = useState("");

  const load = useCallback(async () => {
    try {
      const [b, bd] = await Promise.all([http.get("/bookings"), http.get("/blocked-dates")]);
      setBookings(b.data);
      setBlocked(bd.data);
    } catch {
      toast.error("Could not load bookings.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    try {
      await http.patch(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled.");
      load();
    } catch {
      toast.error("Could not cancel booking.");
    }
  };

  const block = async (e) => {
    e.preventDefault();
    if (!blockDate) return;
    await http.post("/blocked-dates", { date: blockDate });
    toast.success(`${blockDate} blocked.`);
    setBlockDate("");
    load();
  };

  const unblock = async (date) => {
    await http.delete(`/blocked-dates/${date}`);
    load();
  };

  const svcLabel = (s) => (s === "workshop" ? "Workshop (2h)" : "First Conversation (30m)");

  return (
    <div data-testid="bookings-panel">
      <div className="border border-line bg-surface1 p-5 mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mb-3">Block a date (no bookings allowed)</p>
        <form onSubmit={block} className="flex flex-wrap gap-3 items-center">
          <input type="date" data-testid="block-date-input" className="field-dark px-4 py-2.5 text-sm" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} />
          <button type="submit" data-testid="block-date-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 flex items-center gap-2">
            <CalendarX2 size={14} /> Block
          </button>
          <div className="flex flex-wrap gap-2">
            {blocked.map((b) => (
              <span key={b.date} data-testid={`blocked-${b.date}`} className="font-mono text-[11px] px-3 py-1.5 border border-line text-stone2 flex items-center gap-2">
                {b.date}
                <button onClick={() => unblock(b.date)} data-testid={`unblock-${b.date}`} className="text-ember hover:text-emberDeep"><X size={12} /></button>
              </span>
            ))}
          </div>
        </form>
      </div>

      {bookings.length === 0 ? (
        <div className="border border-line bg-surface1 p-16 text-center">
          <Inbox className="mx-auto mb-4 text-stone2" size={32} />
          <p className="text-stone2">No bookings yet.</p>
        </div>
      ) : (
        <div className="space-y-px bg-line/40 border border-line/40">
          {bookings.map((b, i) => (
            <article key={b.id} data-testid={`booking-row-${i}`} className={`bg-ink p-5 md:p-6 flex flex-wrap items-center gap-4 justify-between hover:bg-surface1 transition-colors duration-300 ${b.status === "cancelled" ? "opacity-45" : ""}`}>
              <div className="flex items-baseline gap-4 flex-wrap">
                <span className="font-mono text-sm text-ember w-40">{b.date} · {b.time}</span>
                <span className="font-serif text-lg text-bone">{b.name}</span>
                <a href={`mailto:${b.email}`} className="font-mono text-xs text-stone2 hover:text-ember transition-colors duration-300">{b.email}</a>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border border-ember/40 text-ember">{svcLabel(b.service)}</span>
                {b.organisation && <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border border-line text-stone2">{b.organisation}</span>}
              </div>
              {b.status === "cancelled" ? (
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone2">Cancelled</span>
              ) : (
                <button onClick={() => cancel(b.id)} data-testid={`cancel-booking-${i}`} className="font-mono text-[11px] uppercase tracking-[0.15em] text-stone2 hover:text-ember transition-colors duration-300">
                  Cancel
                </button>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

const LeadsPanel = () => {
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    http.get("/leads")
      .then(({ data }) => setLeads(data))
      .catch(() => toast.error("Could not load leads."));
  }, []);

  return (
    <div data-testid="leads-panel">
      <div className="flex items-baseline justify-between mb-8">
        <h1 className="font-serif text-3xl md:text-4xl">One-pager leads</h1>
        <span data-testid="lead-count" className="font-mono text-sm text-ember">{leads.length} total</span>
      </div>
      {leads.length === 0 ? (
        <div className="border border-line bg-surface1 p-16 text-center">
          <Inbox className="mx-auto mb-4 text-stone2" size={32} />
          <p className="text-stone2">No downloads yet.</p>
        </div>
      ) : (
        <div className="space-y-px bg-line/40 border border-line/40">
          {leads.map((l, i) => (
            <article key={l.id} data-testid={`lead-row-${i}`} className="bg-ink p-5 md:p-6 flex flex-wrap items-baseline gap-4 justify-between hover:bg-surface1 transition-colors duration-300">
              <div className="flex items-baseline gap-4 flex-wrap">
                <span className="font-serif text-lg text-bone">{l.name}</span>
                <a href={`mailto:${l.email}`} className="font-mono text-xs text-ember hover:text-emberDeep transition-colors duration-300">{l.email}</a>
              </div>
              <time className="font-mono text-[11px] uppercase tracking-[0.15em] text-stone2">
                {new Date(l.timestamp).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
              </time>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Admin({ initialTab = "enquiries" }) {
  useNoIndex();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(initialTab);

  const loadEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/enquiries");
      setEnquiries(data);
    } catch {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    http.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) loadEnquiries();
  }, [user, loadEnquiries]);

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    setUser(false);
    setEnquiries([]);
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading…</div>;
  }
  if (!user) return <LoginForm onLogin={setUser} />;

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-line bg-surface1">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/bigibila-logo.png" alt="Bigibila" className="h-9 w-9" />
            <span className="font-serif text-lg">Bigibila</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">Enquiries</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={loadEnquiries} data-testid="admin-refresh-btn" className="font-mono text-xs uppercase tracking-[0.15em] text-stone2 hover:text-ember transition-colors duration-300 flex items-center gap-2">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="font-mono text-xs uppercase tracking-[0.15em] text-stone2 hover:text-ember transition-colors duration-300 flex items-center gap-2">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-12">
        <p data-testid="admin-confidentiality-notice" className="font-mono text-[11px] uppercase tracking-[0.2em] text-ochre border border-ember/40 bg-surface1 px-4 py-3 mb-8">
          Confidential — Bigibila internal. Not for distribution.
        </p>
        <div className="flex items-center gap-6 border-b border-line mb-10 flex-wrap">
          {["enquiries", "bookings", "leads", "briefings", "portal"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`font-mono text-xs uppercase tracking-[0.2em] pb-3 -mb-px border-b-2 transition-colors duration-300 ${
                tab === t ? "border-ember text-ember" : "border-transparent text-stone2 hover:text-bone"
              }`}
            >
              {t}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-3 pb-3">
            {["enquiries", "bookings", "leads"].map((n) => (
              <button key={n} onClick={() => exportCsv(n)} data-testid={`export-${n}-btn`} className="font-mono text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 border border-line text-stone2 hover:border-ember hover:text-ember transition-colors duration-300 flex items-center gap-1.5">
                <Download size={11} /> {n} CSV
              </button>
            ))}
          </div>
        </div>

        {tab === "bookings" ? (
          <BookingsPanel />
        ) : tab === "leads" ? (
          <LeadsPanel />
        ) : tab === "briefings" ? (
          <BriefingsPanel />
        ) : tab === "portal" ? (
          <PortalAdminPanel />
        ) : (
        <>
        <div className="flex items-baseline justify-between mb-8">
          <h1 className="font-serif text-3xl md:text-4xl">Enquiries</h1>
          <span data-testid="enquiry-count" className="font-mono text-sm text-ember">{enquiries.length} total</span>
        </div>

        {enquiries.length === 0 ? (
          <div className="border border-line bg-surface1 p-16 text-center">
            <Inbox className="mx-auto mb-4 text-stone2" size={32} />
            <p className="text-stone2">No enquiries yet.</p>
          </div>
        ) : (
          <div className="space-y-px bg-line/40 border border-line/40">
            {enquiries.map((e, i) => (
              <article key={e.id} data-testid={`enquiry-row-${i}`} className="bg-ink p-6 md:p-8 hover:bg-surface1 transition-colors duration-300">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <div className="flex items-baseline gap-4">
                    <h2 className="font-serif text-xl text-bone">{e.name}</h2>
                    <a href={`mailto:${e.email}`} className="font-mono text-xs text-ember hover:text-emberDeep transition-colors duration-300">{e.email}</a>
                  </div>
                  <time className="font-mono text-[11px] uppercase tracking-[0.15em] text-stone2">
                    {new Date(e.timestamp).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
                  </time>
                </div>
                <div className="flex flex-wrap gap-3 mb-4">
                  {e.organisation && <span className="font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-1 border border-line text-stone2">{e.organisation}</span>}
                  {e.interest && <span className="font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-1 bg-ember/10 border border-ember/40 text-ember">{e.interest}</span>}
                </div>
                <p className="text-sm text-stone2 leading-relaxed max-w-3xl">{e.message}</p>
              </article>
            ))}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
